import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, User, MapPin, ChevronDown, ChevronUp, Filter, X, Users, Briefcase, SlidersHorizontal, Lock } from 'lucide-react';
import { INDUSTRIES, INDUSTRY_DOMAINS, US_STATES, ROLES } from '../constants/industries';
import { usePageHeader } from '../context/PageHeaderContext';

const UserSearchPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [query, setQuery] = useState('');
  // `allUsers` = full unfiltered list cached from `/users/all`. This is
  // what the page is filtered against client-side. Server-side search
  // is reserved for the structured filter dropdowns (role/state/etc.)
  // so the typed-name search can match starting at the first
  // character without a network roundtrip per keystroke.
  const [allUsers, setAllUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const [selectedRole, setSelectedRole] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');

  const isOnboarded = user?.is_onboarded || user?.role === 'admin';
  const availableDomains = selectedIndustry ? INDUSTRY_DOMAINS[selectedIndustry] || [] : [];

  // Mobile header — title becomes "Search Users".
  usePageHeader({ title: 'Search Users' }, []);

  useEffect(() => {
    if (isOnboarded) {
      loadAllUsers();
    } else {
      setLoading(false);
      setInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboarded]);

  // Re-run a server-side search whenever a STRUCTURED filter changes
  // (role/state/industry/domain). The free-text query is filtered
  // entirely client-side below — the backend only sees `q` when it's
  // accompanied by a structured filter (rare).
  useEffect(() => {
    if (initialLoad) return;
    const hasStructuredFilter = !!(selectedRole || selectedState || selectedIndustry || selectedDomain);
    if (hasStructuredFilter) {
      searchUsers();
    } else {
      // No filters → restore the cached full list (then text-filter
      // happens in the next useMemo).
      setUsers(allUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, selectedState, selectedIndustry, selectedDomain]);

  // Free-text filter — applied entirely client-side over whichever
  // result set is currently loaded (`users`). This means typing
  // "S" matches every user with an "s" anywhere in their name/email
  // immediately, with no backend gating, no round-trip latency, and
  // no "still shows all 16" stale-render bug.
  const filteredUsers = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => {
      const haystack = [
        u.first_name,
        u.last_name,
        u.full_name,
        `${u.first_name || ''} ${u.last_name || ''}`,
        u.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [users, query]);

  const loadAllUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/all');
      setAllUsers(res.data);
      setUsers(res.data);
      setInitialLoad(false);
    } catch (error) {
      console.error('Failed to load users:', error);
      setInitialLoad(false);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Backend `q` filter still has a 2-char minimum; we only send it
      // when accompanying a structured filter so the structured filter
      // doesn't return the whole tenant.
      if (query && query.length >= 2) params.append('q', query);
      if (selectedRole) params.append('role', selectedRole);
      if (selectedState) params.append('state', selectedState);
      if (selectedIndustry) params.append('industry', selectedIndustry);
      if (selectedDomain) params.append('industry_domain', selectedDomain);

      const res = await api.get(`/users/search?${params.toString()}`);
      setUsers(res.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedRole('');
    setSelectedState('');
    setSelectedIndustry('');
    setSelectedDomain('');
    setQuery('');
    // Restore the cached full list immediately.
    setUsers(allUsers);
  };

  const hasActiveFilters = selectedRole || selectedState || selectedIndustry || selectedDomain;
  const activeFilterCount = [selectedRole, selectedState, selectedIndustry, selectedDomain].filter(Boolean).length;

  const getInitials = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  const getFullName = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.full_name || 'Unknown User';
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      case 'supervisor': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      case 'lead': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  };

  const handleUserClick = (userId) => {
    navigate(`/profile/${userId}`);
  };

  // Non-onboarded blocker
  if (!isOnboarded) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="user-search-page">
        <div className="max-w-2xl mx-auto pt-4 relative">
          <div className="absolute inset-0 backdrop-blur-md bg-white/50 dark:bg-black/50 rounded-xl z-10 flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Complete Onboarding</h2>
            <p className="text-muted-foreground text-center max-w-sm mb-4">
              You need to complete your onboarding process before searching for users.
            </p>
            <Button onClick={() => navigate('/')}>
              Return to Onboarding
            </Button>
          </div>
          
          {/* Blurred content behind */}
          <div className="opacity-30 pointer-events-none">
            <h1 className="text-2xl font-bold mb-4">Find People</h1>
            <Card className="h-64 bg-white/50 dark:bg-[#141414]/50" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-[#0a0a0a] dark:via-[#0d0d0d] dark:to-[#111]" data-testid="user-search-page">
      <div className="max-w-3xl mx-auto px-4 pt-4 pb-24 relative z-10">
        {/* Search Card */}
        <Card className="border-0 shadow-2xl bg-white/95 dark:bg-[#141414]/95 backdrop-blur-xl mb-4">
          <CardContent className="p-4">
            {/* Search Input */}
            <div className="relative mb-4">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                <Search className="w-4 h-4 text-slate-500" />
              </div>
              <Input
                placeholder="Search by name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-14 pr-10 h-14 text-base bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary/20"
                data-testid="user-search-input"
              />
              {query && (
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                  onClick={() => setQuery('')}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Filter Toggle */}
            <Button 
              variant="outline"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="w-full justify-between h-12 rounded-xl bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-[#252525]"
              data-testid="toggle-filters-btn"
            >
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                <span className="font-medium">Filters</span>
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-white px-2 py-0.5 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </div>
              {filtersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>

            {/* Filters Panel */}
            {filtersOpen && (
              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-4 animate-in slide-in-from-top-2 duration-200">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Role</label>
                    <Select value={selectedRole || "all"} onValueChange={(v) => setSelectedRole(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-11 rounded-lg bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700" data-testid="filter-role">
                        <SelectValue placeholder="All Roles" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        {ROLES.map(role => (
                          <SelectItem key={role} value={role} className="capitalize">{role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">State</label>
                    <Select value={selectedState || "all"} onValueChange={(v) => setSelectedState(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-11 rounded-lg bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700" data-testid="filter-state">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Industry</label>
                    <Select 
                      value={selectedIndustry || "all"} 
                      onValueChange={(v) => {
                        setSelectedIndustry(v === "all" ? "" : v);
                        setSelectedDomain('');
                      }}
                    >
                      <SelectTrigger className="h-11 rounded-lg bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700" data-testid="filter-industry">
                        <SelectValue placeholder="All Industries" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Industries</SelectItem>
                        {INDUSTRIES.map(ind => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Domain</label>
                    <Select 
                      value={selectedDomain || "all"} 
                      onValueChange={(v) => setSelectedDomain(v === "all" ? "" : v)}
                      disabled={!selectedIndustry}
                    >
                      <SelectTrigger className="h-11 rounded-lg bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700 disabled:opacity-50" data-testid="filter-domain">
                        <SelectValue placeholder="All Domains" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Domains</SelectItem>
                        {availableDomains.map(domain => (
                          <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {hasActiveFilters && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="w-full h-10 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear All Filters
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-0 shadow-xl bg-white/90 dark:bg-[#141414]/90 backdrop-blur-xl" data-testid="search-results-card">
          <CardContent className="p-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
                <span className="text-slate-500 text-sm">Searching...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-1">No users found</h3>
                <p className="text-sm text-slate-500 mb-4">Try adjusting your search or filters</p>
                {(hasActiveFilters || query) && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-500">
                    <span className="font-semibold text-slate-700 dark:text-slate-300">{filteredUsers.length}</span> {filteredUsers.length === 1 ? 'person' : 'people'} found
                  </p>
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className="group flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-[#1a1a1a] transition-all cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                      onClick={() => handleUserClick(user.id)}
                      data-testid={`user-result-${user.id}`}
                    >
                      <Avatar className="h-12 w-12 border-2 border-white dark:border-[#1a1a1a] shadow-md group-hover:scale-105 transition-transform">
                        <AvatarImage src={user.profile_picture} />
                        <AvatarFallback className={`text-sm font-bold bg-gradient-to-br ${getRoleGradient(user.role)} text-white`}>
                          {getInitials(user)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 dark:text-white truncate group-hover:text-primary transition-colors">
                          {getFullName(user)}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          {user.industry && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              {user.industry}
                            </span>
                          )}
                          {user.state && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {user.state}
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge className={`text-xs capitalize px-2.5 py-1 border ${getRoleBadgeStyle(user.role)}`}>
                        {user.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
    </div>
  );
};

export default UserSearchPage;
